/**
 * src/stores/useAuthStore.ts
 *
 * File-level:
 * - Centralized auth store using Zustand.
 * - Provides a single source of truth for user, userId and authToken.
 * - Includes helpers to initialize from localStorage and to clear state.
 *
 * Notes:
 * - Uses named import { create } because the installed zustand exposes a named export.
 * - All functions and types include concise JSDoc comments per project rules.
 */

import { create } from 'zustand';

/**
 * AuthUser
 *
 * @interface AuthUser
 * @description Minimal flexible shape for stored user object. Many backend shapes
 *              exist across the project so this remains permissive.
 */
export interface AuthUser {
  userId?: string;
  id?: string;
  profileId?: string;
  basicDetails?: {
    userId?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

/**
 * AuthState
 *
 * @interface AuthState
 * @description Shape of the Zustand auth store. Contains state and actions.
 */
export interface AuthState {
  /** Current user object or null */
  user: AuthUser | null;
  /** Derived or explicit userId */
  userId: string | null;
  /** Auth token string or null */
  authToken: string | null;

  /** Set the full user object and attempt to derive userId from it */
  setUser: (u: AuthUser | null) => void;
  /** Explicitly set the userId */
  setUserId: (id: string | null) => void;
  /** Explicitly set the auth token */
  setAuthToken: (token: string | null) => void;
  /** Clear all auth state */
  clear: () => void;
  /** Initialize store from localStorage without overwriting existing values unnecessarily */
  initializeFromLocalStorage: () => void;
}

/**
 * deriveUserId
 *
 * @function deriveUserId
 * @param u AuthUser | null
 * @returns string | null
 * @description Try common property names to obtain a stable userId from a user object.
 */
function deriveUserId(u: AuthUser | null): string | null {
  if (!u) return null;
  return (
    (u.userId && String(u.userId)) ??
    (u.id && String(u.id)) ??
    (u.profileId && String(u.profileId)) ??
    (u.basicDetails && u.basicDetails.userId && String(u.basicDetails.userId)) ??
    null
  );
}

/**
 * useAuthStore
 *
 * @description Zustand store that holds authentication-related state.
 */
const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  userId: null,
  authToken: null,

  /**
   * setUser
   *
   * @param u AuthUser | null
   * @description Sets the user object and updates userId when possible.
   */
  setUser: (u: AuthUser | null) =>
    set((state) => {
      const resolvedId = deriveUserId(u) ?? state.userId;
      return {
        user: u,
        userId: resolvedId,
      };
    }),

  /**
   * setUserId
   *
   * @param id string | null
   * @description Explicitly set userId.
   */
  setUserId: (id: string | null) =>
    set(() => ({
      userId: id,
    })),

  /**
   * setAuthToken
   *
   * @param token string | null
   * @description Explicitly set auth token.
   */
  setAuthToken: (token: string | null) =>
    set(() => ({
      authToken: token,
    })),

  /**
   * clear
   *
   * @description Clear all auth-related state.
   */
  clear: () =>
    set(() => ({
      user: null,
      userId: null,
      authToken: null,
    })),

  /**
   * initializeFromLocalStorage
   *
   * @description Attempt to populate the store from localStorage keys commonly used
   *              by the project: 'authToken' and 'userData' / 'user'. This function is
   *              safe to call repeatedly (it won't override an existing store user unless absent).
   */
  initializeFromLocalStorage: () => {
    try {
      const current = get();
      // If already populated, avoid overwriting
      const existingUser = current.user;
      const existingToken = current.authToken;

      const token =
        existingToken ??
        (typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null);

      let userData: AuthUser | null = existingUser ?? null;

      if (!userData && typeof localStorage !== 'undefined') {
        const raw = localStorage.getItem('userData') || localStorage.getItem('user') || null;
        if (raw) {
          try {
            userData = JSON.parse(raw) as AuthUser;
          } catch {
            // raw might be a simple id string
            userData = { userId: raw } as AuthUser;
          }
        }
      }

      set((s) => ({
        user: userData ?? s.user,
        userId: deriveUserId(userData) ?? s.userId,
        authToken: token ?? s.authToken,
      }));
    } catch (err) {
      // Keep failure silent but log during development
      // eslint-disable-next-line no-console
      console.warn('[useAuthStore] initializeFromLocalStorage failed', err);
    }
  },
}));

export default useAuthStore;